import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import UniversalCookie from 'universal-cookie';
import config from '../config';
import superagent from 'superagent';
import { useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import { CookiesProvider } from 'react-cookie';
import events from 'events';
export const AUTH_TOKEN = 'auth-token';
export const REFRESH_TOKEN = 'refresh-token';
export type RequestMessage = { [key: string]: any };
export type RequestHeader = { [key: string]: string };
export type ApiType = 'put' | 'get' | 'post' | 'patch' | 'delete';

import { CookieSetOptions } from 'universal-cookie/cjs/types';

export const parseJwt = (token: string) => {
  try {
    return JSON.parse(atob(token.split('.')[1]));
  } catch (e) {
    return null;
  }
};

/** Refresh the access token when it has less than this much lifetime left. */
const REFRESH_LEEWAY_MS = 5 * 60 * 1000;
/** Cookie lifetime for the stored tokens (~30 days, matches Cognito refresh token default). */
const COOKIE_LIFETIME_MS = 30 * 24 * 60 * 60 * 1000;
/** Refresh responses with these statuses mean the session is gone for good. */
const FATAL_REFRESH_STATUSES = [401, 403, 406, 422];

const COOKIE_OPTIONS: CookieSetOptions = { path: '/', domain: '' };

/** Single in-flight refresh shared by all concurrent requests. */
let refreshPromise: Promise<string | undefined> | null = null;

const safeDecode = (token?: string): any => {
  if (!token) {
    return undefined;
  }
  try {
    return jwtDecode(token);
  } catch (e) {
    return undefined;
  }
};

const isExpired = (decoded: any) => !decoded?.exp || decoded.exp * 1000 <= Date.now();
const isExpiringSoon = (decoded: any) =>
  !decoded?.exp || decoded.exp * 1000 - REFRESH_LEEWAY_MS <= Date.now();

export type ConnectionApi = {
  send: <T = any>(
    api: ApiType,
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  post: <T = any>(
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  put: <T = any>(
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  get: <T = any>(path: string, params?: RequestMessage, headers?: any) => Promise<T>;
  patch: <T = any>(
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  delete: <T = any>(
    path: string,
    msg?: RequestMessage,
    params?: RequestMessage,
    headers?: any,
  ) => Promise<T>;
  setToken: (token?: string, refreshToken?: string) => void;
  getToken: () => string;
};

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  image?: string;
  disable: boolean;
  type: number;
};
const ApplicationContext = React.createContext<{
  connection?: ConnectionApi;
  user?: User;
  events: events.EventEmitter;
}>({} as any);
const universalCookie = new UniversalCookie();

const clearAuthCookies = () => {
  universalCookie.remove(AUTH_TOKEN, COOKIE_OPTIONS);
  universalCookie.remove(REFRESH_TOKEN, COOKIE_OPTIONS);
};

const storeTokens = (token: string, refreshToken?: string) => {
  const expires = new Date(Date.now() + COOKIE_LIFETIME_MS);
  universalCookie.set(AUTH_TOKEN, token, { ...COOKIE_OPTIONS, expires });
  if (refreshToken) {
    universalCookie.set(REFRESH_TOKEN, refreshToken, { ...COOKIE_OPTIONS, expires });
  }
};

export const ApplicationProvider: FC<{ children: React.ReactNode }> = (props) => {
  const [token, setToken] = useState<string>(universalCookie.get(AUTH_TOKEN));
  const history = useNavigate();
  const evts = useRef(new events.EventEmitter()).current;

  const getUserInfo = useCallback((token?: string) => {
    if (token) {
      try {
        const user: any = jwtDecode(token);
        return {
          id: user?.['custom:userId'],
          firstName: user.name,
          lastName: user.lastName,
          email: user.email,
          mobile: user.email,
          image: user.image,
          disable: user.disable,
          type: user.type,
        };
      } catch (e) {
        return undefined;
      }
    } else {
      return undefined;
    }
  }, []);

  const [user, setUser] = useState<User | undefined>(getUserInfo(token));

  useEffect(() => {
    const timer = setInterval(() => {
      const newToken = universalCookie.get(AUTH_TOKEN);
      if (token !== newToken) {
        setToken(newToken);
        setUser(getUserInfo(newToken));
      }
    }, 500);
    return () => {
      clearInterval(timer);
    };
  }, [getUserInfo, history, token]);

  /**
   * Calls the backend refresh endpoint. Only one refresh runs at a time; concurrent
   * callers share the same promise. Resolves with the new access token, or with
   * `undefined` when the session cannot be restored (cookies are cleared in that case).
   * On transient failures (network, 5xx) the existing cookies are left untouched.
   */
  const refreshSession = useCallback(
    (url: string, accessToken: string, refreshToken: string): Promise<string | undefined> => {
      if (refreshPromise) {
        return refreshPromise;
      }
      refreshPromise = superagent
        .post(`${url}/users/refresh-session`)
        .set('Content-Type', 'application/json')
        .send({ token: refreshToken, accessToken })
        .then((res) => {
          // Backend wraps the payload as { response: { token, refreshToken } }.
          const data = res.body?.response ?? res.body?.token ?? res.body;
          const newToken: string | undefined = typeof data?.token === 'string' ? data.token : undefined;
          const newRefresh: string | undefined =
            typeof data?.refreshToken === 'string'
              ? data.refreshToken
              : data?.refreshToken?.token ?? refreshToken;
          if (!newToken || !safeDecode(newToken)) {
            throw Object.assign(new Error('Invalid refresh response'), { status: 422 });
          }
          storeTokens(newToken, newRefresh);
          setToken(newToken);
          setUser(getUserInfo(newToken));
          return newToken;
        })
        .catch((err) => {
          const status = err?.status ?? err?.response?.statusCode;
          if (FATAL_REFRESH_STATUSES.includes(status)) {
            clearAuthCookies();
            return undefined;
          }
          // Transient failure: keep the session; the caller may still use the old token.
          return accessToken;
        })
        .finally(() => {
          refreshPromise = null;
        });
      return refreshPromise;
    },
    [getUserInfo],
  );

  /**
   * Returns a token that is safe to send with a request, refreshing it first if it is
   * expired or about to expire. Returns `undefined` when there is no session.
   */
  const ensureFreshToken = useCallback(
    async (url: string, force = false): Promise<string | undefined> => {
      const accessToken: string | undefined = universalCookie.get(AUTH_TOKEN);
      const refreshToken: string | undefined = universalCookie.get(REFRESH_TOKEN);
      if (!accessToken) {
        return undefined;
      }
      const decoded = safeDecode(accessToken);
      if (!force && decoded && !isExpiringSoon(decoded)) {
        return accessToken;
      }
      if (!refreshToken) {
        // Nothing to refresh with. Only drop the session once the token is really dead.
        if (isExpired(decoded)) {
          clearAuthCookies();
          return undefined;
        }
        return accessToken;
      }
      return refreshSession(url, accessToken, refreshToken);
    },
    [refreshSession],
  );

  const sendRequest = useCallback(
    async (
      method: (url: string) => superagent.SuperAgentRequest,
      path: string,
      msg?: RequestMessage,
      query?: RequestMessage,
      headers?: RequestHeader,
    ) => {
      const url = `${config.api.baseUrl}`;

      let jsonString = '';
      try {
        jsonString = JSON.stringify(msg);
      } catch (e) {
        throw Error('Invalid JSON object');
      }

      const execute = (authToken?: string) => {
        const req = method(`${url}/${path}`).set('Content-Type', 'application/json');
        if (query) {
          req.query(query);
        }
        if (authToken) {
          req.set('Authorization', `Bearer ${authToken}`);
        }
        if (headers) {
          Object.keys(headers).forEach((key) => req.set(key, headers[key]));
        }
        return req.send(jsonString);
      };

      const toError = (res: any) => {
        const response = res?.response;
        if (response?.statusCode || response?.status) {
          const status = response.status ?? response.statusCode;
          const { statusText } = response;
          return {
            status,
            statusText: statusText ?? `${status}`,
            message: response.body?.message ?? (statusText ? statusText : 'Unknown'),
            body: response.body,
          };
        }
        return {
          status: 500,
          statusCode: 500,
          statusText: 'Unknown',
          code: 'Unknown',
          message: res?.message ?? 'Unknown',
        };
      };

      const authToken = await ensureFreshToken(url);
      try {
        const res: any = await execute(authToken);
        return res.body;
      } catch (err: any) {
        // The server rejected our token even though it looked valid locally
        // (clock skew, revoked session...). Force one refresh and retry once.
        if ((err?.status ?? err?.response?.statusCode) === 401 && authToken) {
          const retryToken = await ensureFreshToken(url, true);
          if (retryToken && retryToken !== authToken) {
            try {
              const res: any = await execute(retryToken);
              return res.body;
            } catch (retryErr: any) {
              throw toError(retryErr);
            }
          }
        }
        throw toError(err);
      }
    },
    [ensureFreshToken],
  );

  const post = useCallback(
    (path: string, msg?: RequestMessage, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.post, path, msg, query, headers);
    },
    [sendRequest],
  );
  const put = useCallback(
    (path: string, msg?: RequestMessage, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.put, path, msg, query, headers);
    },
    [sendRequest],
  );
  const get = useCallback(
    (path: string, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.get, path, undefined, query, headers);
    },
    [sendRequest],
  );
  const patch = useCallback(
    (path: string, msg?: RequestMessage, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.patch, path, msg, query, headers);
    },
    [sendRequest],
  );
  const del = useCallback(
    (path: string, msg?: RequestMessage, query?: RequestMessage, headers?: RequestHeader) => {
      return sendRequest(superagent.delete, path, msg, query, headers);
    },
    [sendRequest],
  );

  const send = useCallback(
    (api: ApiType, path: string, msg?: RequestMessage, params?: RequestMessage, headers?: any) => {
      switch (api) {
        case 'post':
          return post(path, msg, params, headers);
        case 'get':
          return get(path, params ?? msg, headers);
        case 'put':
          return put(path, msg, params, headers);
        case 'patch':
          return patch(path, msg, params, headers);
        case 'delete':
          return del(path, msg, params, headers);
      }
    },
    [del, get, post, put, patch],
  );

  const updateToken = useCallback(
    (token?: string, refreshToken?: string) => {
      if (token) {
        if (!safeDecode(token)) {
          console.error('setToken: received an invalid JWT');
          return;
        }
        storeTokens(token, refreshToken);
        setToken(token);
        setUser(getUserInfo(token));
      } else {
        clearAuthCookies();
        setToken(undefined as any);
        setUser(undefined);
      }
    },
    [getUserInfo],
  );
  const getToken = useCallback(() => {
    if (token) {
      return token;
    } else {
      return '';
    }
  }, [token]);
  const connection: ConnectionApi = useMemo(
    () => ({ send, post, put, get, patch, delete: del, setToken: updateToken, getToken }),
    [send, post, put, get, patch, del, updateToken, getToken],
  );
  return (
    <CookiesProvider>
      <ApplicationContext.Provider
        value={{
          connection,
          user,
          events: evts,
        }}>
        {props.children}
      </ApplicationContext.Provider>
    </CookiesProvider>
  );
};

export default ApplicationContext;
